import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Sparkles, Upload, CheckCircle, TrendingUp, BarChart2, Target, Zap, Users, Star } from 'lucide-react';

const MU_LOGO = 'https://customer-assets.emergentagent.com/job_career-heat-map/artifacts/8zniuor4_MU%20Logo.png';

/* ─── Flip heading — isolated so only the text re-renders, not the whole page ── */
const FLIP_WORDS = ['Underpaid?', 'Ready for MBA?', 'Switching Careers?'];
const FlipHeading = () => {
  const [idx, setIdx]   = useState(0);
  const [key, setKey]   = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx(i => (i + 1) % FLIP_WORDS.length);
      setKey(k => k + 1);
    }, 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ marginBottom: '24px' }}>
      <h1 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(38px, 5.5vw, 68px)', fontWeight: 900, color: '#111111', letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0 }}>
        Are You
      </h1>
      <div style={{ perspective: '600px', overflow: 'hidden', height: 'clamp(52px, 7.5vw, 90px)', marginTop: '2px' }}>
        <span
          key={key}
          className="flip-word"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 600, fontSize: 'clamp(38px, 5.5vw, 68px)', color: '#E8A020', lineHeight: 1.05, letterSpacing: '-0.02em', display: 'inline-block' }}
        >
          {FLIP_WORDS[idx]}
        </span>
      </div>
    </div>
  );
};

/* ─── Animated counter ────────────────────────────────────────────────────── */
const Counter = ({ value, prefix = '', suffix = '', decimals = 0 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const end = value;
    const duration = 1800;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start = Math.min(start + step, end);
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  const formatted = decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toLocaleString('en-IN');
  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
};

/* ─── Fade-in wrapper ─────────────────────────────────────────────────────── */
const FadeIn = ({ children, delay = 0, direction = 'up', className, style }) => (
  <motion.div
    className={className}
    style={style}
    initial={{ opacity: 0, y: direction === 'up' ? 32 : direction === 'down' ? -32 : 0, x: direction === 'left' ? 40 : direction === 'right' ? -40 : 0 }}
    whileInView={{ opacity: 1, y: 0, x: 0 }}
    transition={{ duration: 0.55, delay, ease: [0.2, 0.8, 0.2, 1] }}
    viewport={{ once: true, margin: '-60px' }}
  >
    {children}
  </motion.div>
);

/* ─── Report Card Mockups ─────────────────────────────────────────────────── */
const EarningGapMockup = () => (
  <div style={{ backgroundColor: '#FFFCF6', border: '1.5px solid rgba(232,160,32,0.22)', borderRadius: '14px', padding: '16px', fontSize: '14.5px', fontFamily: 'DM Sans, sans-serif' }}>
    <div style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Earning Potential Gap</div>
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
      {[{ l: 'You now', v: '₹15L', c: '#111' }, { l: 'Market rate', v: '₹28L', c: '#C47A00' }, { l: 'Post-PGP', v: '₹45L', c: '#2A7050' }].map((box, i) => (
        <React.Fragment key={i}>
          <div style={{ backgroundColor: '#ffffff', border: `1px solid ${i === 1 ? 'rgba(232,160,32,0.30)' : i === 2 ? 'rgba(63,168,106,0.25)' : 'rgba(0,0,0,0.09)'}`, borderRadius: '10px', padding: '8px 10px', textAlign: 'center', minWidth: '60px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, color: box.c }}>{box.v}</div>
            <div style={{ fontSize: '9px', color: 'rgba(0,0,0,0.38)', marginTop: '2px' }}>{box.l}</div>
          </div>
          {i < 2 && <div style={{ color: 'rgba(0,0,0,0.20)', fontSize: '14.5px' }}>→</div>}
        </React.Fragment>
      ))}
    </div>
    <div style={{ backgroundColor: 'rgba(217,64,64,0.07)', border: '1px solid rgba(217,64,64,0.20)', borderRadius: '8px', padding: '7px 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '14.5px', fontWeight: 900, color: '#D94040' }}>33%</span>
      <span style={{ fontSize: '10px', color: 'rgba(0,0,0,0.55)' }}>below market rate · ₹7.5L/year gap</span>
    </div>
  </div>
);

const CareerHealthMockup = () => (
  <div style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', overflow: 'hidden', fontFamily: 'DM Sans, sans-serif' }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: '9px', fontWeight: 700, color: 'rgba(0,0,0,0.35)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>KEY FINDINGS</div>
        <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#111' }}>Your career health at a glance</div>
      </div>
      <div style={{ fontSize: '14.5px', fontWeight: 900, color: '#2A7050' }}>₹45L</div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      {[
        { label: "Already working", text: "₹30L/month budget ownership at scale", color: '#2A7050', arrow: '↗' },
        { label: "Market rewards", text: "Growth marketers at this level earn ₹30-50L", color: '#2A7050', arrow: '↗' },
        { label: "Breakout potential", text: "P&L ownership unlocks GM-level trajectory", color: '#C47A00', arrow: '⚡' },
        { label: "Watch out for", text: "No C-suite exposure yet on your CV", color: '#C03030', arrow: '↘' },
      ].map((c, i) => (
        <div key={i} style={{ padding: '10px 12px', borderRight: i % 2 === 0 ? '1px solid rgba(0,0,0,0.06)' : 'none', borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '8px', fontWeight: 700, color: 'rgba(0,0,0,0.32)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{c.label}</span>
            <span style={{ fontSize: '14.5px', color: c.color }}>{c.arrow}</span>
          </div>
          <p style={{ fontSize: '10px', fontWeight: 600, color: c.color, margin: 0, lineHeight: 1.4 }}>{c.text}</p>
        </div>
      ))}
    </div>
  </div>
);

const CareerPathMockup = () => (
  <div style={{ display: 'flex', gap: '8px', fontFamily: 'DM Sans, sans-serif' }}>
    {[
      { title: 'Head of Growth', match: 78, salary: '₹35–80L', co: ['Zomato', 'CRED', 'Zepto'] },
      { title: 'Strategy Consultant', match: 68, salary: '₹18–45L', co: ['BCG', 'Bain', 'McKinsey'] },
    ].map((r, i) => (
      <div key={i} style={{ flex: 1, backgroundColor: '#ffffff', border: '1.5px solid rgba(0,0,0,0.09)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, #E8A020, #F0B840)' }} />
        <div style={{ padding: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#111', lineHeight: 1.3, flex: 1 }}>{r.title}</div>
            <span style={{ backgroundColor: '#E8A020', color: '#fff', fontSize: '8px', fontWeight: 800, padding: '2px 5px', borderRadius: '9999px', whiteSpace: 'nowrap', marginLeft: '4px' }}>{r.match}%</span>
          </div>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#C47A00', marginBottom: '6px' }}>{r.salary}</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {r.co.map(c => <span key={c} style={{ fontSize: '8px', backgroundColor: '#f5f5f5', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '9999px', padding: '2px 6px', color: '#333' }}>{c}</span>)}
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SnapshotMockup = () => (
  <div style={{ background: 'radial-gradient(ellipse 120% 60% at 50% 0%, #231B0D, #0C0C0C)', borderRadius: '14px', padding: '16px', fontFamily: 'DM Sans, sans-serif', color: '#fff' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#E8A020' }}>THE SNAPSHOT</span>
      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
    </div>
    <p style={{ fontSize: '14px', fontWeight: 700, color: '#fff', lineHeight: 1.35, marginBottom: '10px', margin: '0 0 10px' }}>
      "Ran ₹30L/month growth engine — now needs boardroom strategy to match the hustle."
    </p>
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {[{ l: '9/10', t: 'Leadership', c: '#E8A020' }, { l: '8/10', t: 'Commercial Acumen', c: '#E8A020' }, { l: '7/10', t: 'Strategic Thinking', c: 'rgba(255,255,255,0.45)' }].map(b => (
        <div key={b.t} style={{ backgroundColor: `${b.c === '#E8A020' ? 'rgba(232,160,32,0.12)' : 'rgba(255,255,255,0.06)'}`, border: `1px solid ${b.c === '#E8A020' ? 'rgba(232,160,32,0.28)' : 'rgba(255,255,255,0.10)'}`, borderRadius: '9999px', padding: '3px 8px', display: 'flex', gap: '4px', alignItems: 'center' }}>
          <span style={{ fontSize: '10px', fontWeight: 800, color: b.c }}>{b.l}</span>
          <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.50)' }}>{b.t}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ─── LANDING PAGE ────────────────────────────────────────────────────────── */
const LandingPage = ({ onGetStarted }) => {
  /* ── HERO ─────────────────────────────────────────────────────────────── */
  const Hero = () => (
    <section style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 'clamp(80px,12vw,120px) clamp(20px,6vw,80px) 60px',
      background: 'radial-gradient(ellipse 130% 50% at 50% 0%, #FFF7E6 0%, #FFF9F2 30%, #fafafa 65%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Ambient circles */}
      <div style={{ position: 'absolute', top: '-100px', right: '-80px', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,160,32,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '0', left: '-60px', width: '350px', height: '350px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(63,168,106,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '48px', alignItems: 'center' }} className="hero-grid">
        {/* Left */}
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ marginBottom: '24px' }}>

            {/* Kicker pill with BETA badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.10)', borderRadius: '9999px', padding: '5px 14px', marginBottom: '28px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <img src={MU_LOGO} alt="MU" style={{ height: '14px', objectFit: 'contain' }} />
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 600, color: 'rgba(0,0,0,0.55)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Career Analysis</span>
              <span style={{ backgroundColor: '#E8A020', color: '#ffffff', fontSize: '8px', fontWeight: 800, padding: '2px 6px', borderRadius: '9999px', letterSpacing: '0.07em', lineHeight: '14px' }}>BETA</span>
            </div>

            {/* Flipping headline — isolated component */}
            <FlipHeading />

            {/* New subheading with bullet list */}
            <div style={{ marginBottom: '32px', maxWidth: '480px' }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: 'rgba(0,0,0,0.58)', lineHeight: 1.6, marginBottom: '14px' }}>
                Unlock personalised career report in under ~60s with your resume and find out:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {['Your earning potential', 'Current market value', 'Career paths for you', 'Whether you should do an MBA'].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#3FA86A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4l2 2L6.5 2" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(13px, 1.5vw, 16px)', fontWeight: 500, color: '#111111' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust chips — updated labels */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '36px' }}>
              {[
                { k: '~60 second analysis', v: '' },
                { k: 'Personalised', v: 'role recommendations' },
                { k: 'AI-powered', v: 'Deep analysis' },
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '9999px', padding: '7px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  {t.v && <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.38)' }}>{t.v}</span>}
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#111' }}>{t.k}</span>
                </div>
              ))}
            </div>

            <button onClick={onGetStarted} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 32px', backgroundColor: '#E8A020', border: 'none', borderRadius: '9999px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#ffffff', boxShadow: '0 4px 20px rgba(232,160,32,0.35)', transition: 'transform 0.15s ease, box-shadow 0.2s ease' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(232,160,32,0.45)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,160,32,0.35)'; }}>
              <Sparkles size={17} />
              Analyse my resume
              <ArrowRight size={17} />
            </button>

            {/* Social proof row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '18px', flexWrap: 'wrap' }}>
              {/* Overlapping avatar circles */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {[
                  { l: 'V', c: '#4F8EF7' },
                  { l: 'R', c: '#7B4FD4' },
                  { l: 'S', c: '#E84F6B' },
                  { l: 'P', c: '#3FA86A' },
                ].map((a, i) => (
                  <div key={i} style={{
                    width: '30px', height: '30px', borderRadius: '50%',
                    backgroundColor: a.c, color: '#ffffff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '14px', fontWeight: 800,
                    marginLeft: i > 0 ? '-9px' : '0',
                    border: '2px solid #FFF8EC',
                    position: 'relative', zIndex: 4 - i,
                    fontFamily: 'DM Sans, sans-serif',
                  }}>
                    {a.l}
                  </div>
                ))}
                <div style={{
                  width: '30px', height: '30px', borderRadius: '50%',
                  backgroundColor: '#f0f0f0', color: 'rgba(0,0,0,0.50)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '8px', fontWeight: 800,
                  marginLeft: '-9px', border: '2px solid #FFF8EC',
                  position: 'relative', zIndex: 0,
                  fontFamily: 'DM Sans, sans-serif',
                }}>
                  +2.8k
                </div>
              </div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.52)', margin: 0, lineHeight: 1.4 }}>
                <strong style={{ color: '#111111', fontWeight: 700 }}>2,847</strong>{' '}
                users evaluated on their next move
              </p>
            </div>

            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.32)', marginTop: '12px' }}>
              Free forever · Your data stays private
            </p>
          </motion.div>
        </div>

        {/* Right — floating report preview */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          style={{ position: 'relative' }}
          className="hero-preview-col"
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ position: 'relative' }}
          >
            {/* Main report card preview */}
            <div style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '20px', padding: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.12)', overflow: 'hidden', position: 'relative' }}>
              {/* Report header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.38)', marginBottom: '2px' }}>Career Analysis Report</p>
                  <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#111' }}>
                    Your Career <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', color: '#E8A020' }}>Snapshot</span>
                  </h3>
                </div>
                <div style={{ backgroundColor: 'rgba(232,160,32,0.10)', border: '1px solid rgba(232,160,32,0.22)', borderRadius: '9999px', padding: '4px 10px', fontSize: '10px', fontWeight: 700, color: '#9A6200', fontFamily: 'DM Sans, sans-serif' }}>BETA</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <EarningGapMockup />
                <CareerHealthMockup />
                <CareerPathMockup />
              </div>
              {/* Fade out bottom */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px', background: 'linear-gradient(to bottom, transparent, #ffffff)', borderRadius: '0 0 20px 20px', pointerEvents: 'none' }} />
            </div>

            {/* Floating accent chips — hidden on mobile to prevent overflow */}
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              className="hero-float-chip"
              style={{ position: 'absolute', top: '-16px', right: '-16px', backgroundColor: '#ffffff', border: '1px solid rgba(63,168,106,0.25)', borderRadius: '12px', padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'DM Sans, sans-serif' }}
            >
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3FA86A' }} />
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#111' }}>₹45L post-PGP potential</span>
            </motion.div>

            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              className="hero-float-chip"
              style={{ position: 'absolute', bottom: '20px', left: '-20px', backgroundColor: '#ffffff', border: '1px solid rgba(232,160,32,0.22)', borderRadius: '12px', padding: '8px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', fontFamily: 'DM Sans, sans-serif' }}
            >
              <p style={{ fontSize: '10px', color: 'rgba(0,0,0,0.38)', margin: '0 0 2px' }}>Top match</p>
              <p style={{ fontSize: '14.5px', fontWeight: 800, color: '#111', margin: 0 }}>Head of Growth · 78%</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
          <div style={{ width: '24px', height: '40px', border: '2px solid rgba(0,0,0,0.15)', borderRadius: '12px', margin: '0 auto', display: 'flex', justifyContent: 'center', paddingTop: '6px' }}>
            <div style={{ width: '3px', height: '8px', borderRadius: '9999px', backgroundColor: '#E8A020' }} />
          </div>
        </motion.div>
      </div>
    </section>
  );

  /* ── STATS STRIP ────────────────────────────────────────────────────────── */
  const StatsStrip = () => (
    <section style={{ backgroundColor: '#111111', padding: '28px clamp(20px,6vw,80px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0' }}>
        {[
          { value: 33.39, prefix: '₹', suffix: 'L', decimals: 2, label: 'avg post-PGP CTC' },
          { value: 145, prefix: '', suffix: '+', decimals: 0, label: 'companies hiring' },
          { value: 60, prefix: '~', suffix: 's', decimals: 0, label: 'to get your report' },
          { value: 6, prefix: '', suffix: '', decimals: 0, label: 'unique roles mapped' },
        ].map((s, i) => (
          <div key={i} style={{ flex: '1 1 160px', textAlign: 'center', padding: '8px 24px', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.10)' : 'none' }}>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(22px, 3vw, 32px)', fontWeight: 900, color: '#E8A020', letterSpacing: '-0.02em', lineHeight: 1 }}>
              <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} />
            </div>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(255,255,255,0.45)', marginTop: '4px', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );

  /* ── WHAT'S INSIDE ──────────────────────────────────────────────────────── */
  const WhatInside = () => {
    const sections = [
      {
        icon: TrendingUp,
        color: '#D94040',
        title: 'Earning Potential Gap',
        desc: 'See exactly how underpaid you are vs the market, with real 2026 salary benchmarks and your post-PGP earning trajectory.',
        mockup: <EarningGapMockup />,
      },
      {
        icon: Zap,
        color: '#E8A020',
        title: 'Career Health Check',
        desc: '4 sharp, personalised insights — what\'s working, what the market rewards, your breakout lever, and the one thing to fix.',
        mockup: <CareerHealthMockup />,
      },
      {
        icon: BarChart2,
        color: '#E8A020',
        title: 'Competency Benchmark',
        desc: 'A spider graph of 6-8 core competencies showing your current level vs. excellence — with actionable insights for each.',
        mockup: <SnapshotMockup />,
      },
      {
        icon: Target,
        color: '#2A7050',
        title: '6 Career Path Suggestions',
        desc: 'Specific roles you should be targeting — with salary ranges, top Indian companies hiring, and growth trajectories.',
        mockup: <CareerPathMockup />,
      },
    ];

    return (
      <section style={{ padding: 'clamp(60px,10vw,100px) clamp(20px,6vw,80px)', backgroundColor: '#fafafa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', marginBottom: '12px' }}>What you'll find inside</p>
              <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#111111', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '14px' }}>
                A career report that{' '}
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
                  actually tells you something
                </span>
              </h2>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: 'rgba(0,0,0,0.50)', maxWidth: '560px', margin: '0 auto', lineHeight: 1.6 }}>
                Not generic MBA advice. A personalised analysis of YOUR resume, benchmarked against real market data.
              </p>
            </div>
          </FadeIn>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {sections.map((s, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ padding: '20px 20px 16px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: `${s.color}15`, border: `1px solid ${s.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                      <s.icon size={17} color={s.color} />
                    </div>
                    <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#111111', marginBottom: '8px', lineHeight: 1.2 }}>{s.title}</h3>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.55)', lineHeight: 1.6, marginBottom: '16px' }}>{s.desc}</p>
                  </div>
                  <div style={{ padding: '0 16px 18px', marginTop: 'auto' }}>
                    {s.mockup}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    );
  };

  /* ── HOW IT WORKS ───────────────────────────────────────────────────────── */
  const HowItWorks = () => (
    <section style={{ padding: 'clamp(60px,10vw,100px) clamp(20px,6vw,80px)', background: 'radial-gradient(ellipse 90% 50% at 50% 100%, #FFF7E6 0%, #fafafa 60%)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: '52px' }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.35)', marginBottom: '12px' }}>How it works</p>
            <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#111111', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Three steps to{' '}
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
                career clarity
              </span>
            </h2>
          </div>
        </FadeIn>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }} className="steps-grid">
          {[
            { step: '01', icon: Upload, title: 'Upload your resume', desc: 'Drop your PDF, DOCX, or TXT file. Our AI reads your full work history, skills, and achievements.', color: '#E8A020' },
            { step: '02', icon: Users, title: 'Add your details', desc: 'Share your email, phone, and current CTC. This lets us personalise the salary gap analysis.', color: '#E8A020' },
            { step: '03', icon: Star, title: 'Get your career report', desc: 'In ~30 seconds, your full AI analysis is ready — earning gap, competency benchmark, and 6 tailored career paths.', color: '#3FA86A' },
          ].map((s, i) => (
            <FadeIn key={i} delay={i * 0.12}>
              <div style={{ textAlign: 'center', padding: '28px 24px', backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.07)', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', position: 'relative' }}>
                {i < 2 && (
                  <div style={{ position: 'absolute', right: '-13px', top: '50%', transform: 'translateY(-50%)', zIndex: 1, display: 'flex', alignItems: 'center' }} className="step-arrow">
                    <div style={{ width: '26px', height: '26px', backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <ArrowRight size={12} color="rgba(0,0,0,0.30)" />
                    </div>
                  </div>
                )}
                <div style={{ fontSize: '14.5px', fontWeight: 800, color: s.color, fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.06em', marginBottom: '14px' }}>{s.step}</div>
                <div style={{ width: '48px', height: '48px', borderRadius: '14px', backgroundColor: `${s.color}12`, border: `1.5px solid ${s.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <s.icon size={22} color={s.color} />
                </div>
                <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#111', marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: 'rgba(0,0,0,0.52)', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.4}>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <button onClick={onGetStarted} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '14px 28px', backgroundColor: '#E8A020', border: 'none', borderRadius: '9999px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#ffffff', boxShadow: '0 3px 14px rgba(232,160,32,0.30)', transition: 'transform 0.15s ease' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
              Start for free
              <ArrowRight size={15} />
            </button>
          </div>
        </FadeIn>
      </div>
    </section>
  );

  /* ── DARK FEATURE ───────────────────────────────────────────────────────── */
  const DarkFeature = () => (
    <section style={{ background: 'radial-gradient(ellipse 120% 60% at 50% 0%, #231B0D 0%, #141109 40%, #060504 70%, #0C0C0C 100%)', padding: 'clamp(60px,10vw,100px) clamp(20px,6vw,80px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '48px', alignItems: 'center' }} className="dark-grid">
        {/* Left text */}
        <FadeIn direction="right">
          <div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#E8A020', marginBottom: '16px' }}>The Snapshot</p>
            <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(26px, 3.5vw, 40px)', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '18px' }}>
              Blunt. Personal.{' '}
              <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
                Uncomfortably accurate.
              </span>
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.5vw, 16px)', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: '28px' }}>
              "The Snapshot" section gives you the unfiltered read — what's already working in your favour, where the market will pay a premium, and the one specific thing holding your ceiling down.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                'Specific to YOUR resume — not template advice',
                'References your actual companies, metrics, and roles',
                'Written like a sharp mentor, not a career chatbot',
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#3FA86A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                    <CheckCircle size={10} color="#fff" />
                  </div>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(255,255,255,0.62)', lineHeight: 1.5 }}>{t}</span>
                </div>
              ))}
            </div>
            <button onClick={onGetStarted} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 26px', backgroundColor: 'transparent', border: '1.5px solid rgba(232,160,32,0.55)', borderRadius: '9999px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 600, color: '#E8A020', marginTop: '28px', transition: 'background-color 0.2s ease' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(232,160,32,0.10)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              Get my snapshot <ArrowRight size={14} />
            </button>
          </div>
        </FadeIn>

        {/* Right — Snapshot mockup */}
        <FadeIn direction="left" delay={0.1}>
          <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}>
            <div style={{ background: 'radial-gradient(ellipse 110% 60% at 50% 0%, #2A1F0A, #0C0C0C)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '18px', padding: '24px', boxShadow: '0 24px 60px rgba(0,0,0,0.50)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#E8A020' }}>THE SNAPSHOT</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.06)' }} />
              </div>
              <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(16px,2vw,22px)', fontWeight: 700, color: '#ffffff', lineHeight: 1.25, marginBottom: '20px', letterSpacing: '-0.01em' }}>
                "Ran ₹30L/month growth Now needs the boardroom vocabulary to match the street instincts."
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                {[
                  { label: "WHERE YOU'RE DANGEROUS", text: "2300 leads/week is director-level output. Most MBAs never touch this scale.", arrow: '↗', c: '#2A7050' },
                  { label: "MARKET PAYS FOR THIS", text: "Growth + edtech + budget ownership = ₹35-55L hiring surge right now.", arrow: '↗', c: '#2A7050' },
                  { label: "BREAKOUT POTENTIAL", text: "One P&L ownership line separates you from a VP-track offer.", arrow: '⚡', c: '#C47A00' },
                  { label: "WATCH OUT FOR", text: "Campaign scaling ≠ company scaling. Senior roles will ask for the latter.", arrow: '↘', c: '#C03030' },
                ].map((c, i) => (
                  <div key={i} style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.28)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{c.label}</span>
                      <span style={{ fontSize: '14.5px', color: c.c }}>{c.arrow}</span>
                    </div>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.45, margin: 0 }}>{c.text}</p>
                  </div>
                ))}
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '14px' }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: '14px', color: 'rgba(255,255,255,0.45)', margin: 0, lineHeight: 1.5 }}>
                  "Your profile is one structured programme away from the title your output already deserves."
                </p>
              </div>
            </div>
          </motion.div>
        </FadeIn>
      </div>
    </section>
  );

  /* ── FINAL CTA ──────────────────────────────────────────────────────────── */
  const FinalCTA = () => (
    <section style={{ padding: 'clamp(60px,10vw,100px) clamp(20px,6vw,80px)', background: 'radial-gradient(ellipse 100% 60% at 50% 50%, #FFF7E6 0%, #fafafa 70%)' }}>
      <FadeIn>
        <div style={{ maxWidth: '660px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '14px', backgroundColor: '#E8A020', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <img src="https://customer-assets.emergentagent.com/job_career-heat-map/artifacts/9tou0chz_Untitled%20design%20%2813%29.png" alt="" style={{ width: '28px', height: '28px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
          </div>
          <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, color: '#111111', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '16px' }}>
            Ready to see where your career{' '}
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>
              is actually headed?
            </span>
          </h2>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: 'rgba(0,0,0,0.52)', lineHeight: 1.65, marginBottom: '32px' }}>
            Upload your resume and get a full, AI-powered career analysis in under 60 seconds. No account required. Your data stays private.
          </p>
          <button onClick={onGetStarted} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '17px 36px', backgroundColor: '#E8A020', border: 'none', borderRadius: '9999px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', fontWeight: 700, color: '#ffffff', boxShadow: '0 4px 20px rgba(232,160,32,0.35)', transition: 'transform 0.15s ease, box-shadow 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(232,160,32,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,160,32,0.35)'; }}>
            <Sparkles size={17} />
            Analyse my resume — it's free
            <ArrowRight size={17} />
          </button>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '20px', flexWrap: 'wrap' }}>
            {['Your data stays private', 'No account needed', '~60 seconds'].map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#3FA86A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4l2 2L6.5 2.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: 'rgba(0,0,0,0.45)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>
    </section>
  );

  return (
    <div style={{ backgroundColor: '#fafafa', overflowX: 'hidden' }}>
      <Hero />
      <StatsStrip />
      <WhatInside />
      <HowItWorks />
      <DarkFeature />
      <FinalCTA />
    </div>
  );
};

export default LandingPage;
